import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import { TextInput } from 'react-native';
import useColors from 'hooks/useColors';
import { applyMaskToText, caretAfterMask, deleteDigitBeforeSeparator, getMoneyKeyboardType, normalizeTypedText, unmaskText, } from 'utils/moneyMask';
import { unavailableOnWeb } from './unavailableOnWeb.web';
export { MAX_INTEGER_DIGITS } from 'utils/moneyMask';
class MoneyInputError extends Error {
    constructor(msg) {
        super(`[MoneyInput]: ${msg}`);
    }
}
export const applyMask = unavailableOnWeb('MoneyInput.applyMask');
export const unmount = unavailableOnWeb('MoneyInput.unmount');
export const mask = (text, options) => Promise.resolve(applyMaskToText(text, options));
export const unmask = (text, options) => Promise.resolve(unmaskText(text, options));
/**
 * Web version of `MoneyTextInput`: same props, callbacks and app defaults as
 * the native wrapper, with the masking done in JS by `utils/moneyMask`.
 */
export const MoneyTextInput = forwardRef(({ groupingSeparator = ',', fractionSeparator = '.', suffix, prefix, maximumFractionalDigits = 2, maximumIntegerDigits, minValue, maxValue, onChangeText, value, formatValue: _formatValue, ...props }, ref) => {
    const maskOptions = useMemo(() => {
        if (fractionSeparator === '') {
            throw new MoneyInputError('The property `fractionSeparator` cannot be an empty string');
        }
        if (typeof minValue === 'number' &&
            typeof maxValue === 'number' &&
            minValue >= maxValue) {
            throw new MoneyInputError('The property `minValue` must be strictly less than `maxValue`');
        }
        return {
            groupingSeparator,
            fractionSeparator,
            suffix,
            prefix,
            maximumIntegerDigits,
            maximumFractionalDigits,
            minValue,
            maxValue,
        };
    }, [
        fractionSeparator,
        groupingSeparator,
        suffix,
        prefix,
        maximumIntegerDigits,
        maximumFractionalDigits,
        minValue,
        maxValue,
    ]);
    const colors = useColors();
    const inputRef = useRef(null);
    const [internalValue, setInternalValue] = useState(() => applyMaskToText(value ?? '', {
        ...maskOptions,
        needBeforeUnmasking: false,
    }));
    const [selection, setSelection] = useState();
    const syncedValueRef = useRef(value);
    const appliedOptionsRef = useRef(maskOptions);
    const [emitCount, setEmitCount] = useState(0);
    useImperativeHandle(ref, () => inputRef.current);
    useEffect(() => {
        const optionsChanged = appliedOptionsRef.current !== maskOptions;
        appliedOptionsRef.current = maskOptions;
        if (!optionsChanged &&
            (value === undefined || value === syncedValueRef.current)) {
            return;
        }
        syncedValueRef.current = value;
        setInternalValue(applyMaskToText(value ?? '', {
            ...maskOptions,
            needBeforeUnmasking: false,
        }));
    }, [value, maskOptions, emitCount]);
    const changeValueHandler = useCallback((text) => {
        const { text: typed, caret: typedCaret } = deleteDigitBeforeSeparator(normalizeTypedText(text, internalValue, maskOptions), inputRef.current?.selectionStart ??
            text.length, internalValue, maskOptions);
        const masked = applyMaskToText(typed, maskOptions);
        const caret = caretAfterMask(typed, typedCaret, masked, maskOptions);
        const extracted = unmaskText(masked, maskOptions);
        setInternalValue(masked);
        setSelection({ start: caret, end: caret });
        syncedValueRef.current = extracted;
        onChangeText?.(masked, extracted);
        setEmitCount((count) => count + 1);
    }, [internalValue, maskOptions, onChangeText]);
    return (<TextInput ref={inputRef} allowFontScaling={false} selectionColor={colors.text.selection} placeholderTextColor={colors.text.placeholder} {...props} keyboardType={getMoneyKeyboardType(maximumFractionalDigits)} selection={selection} onChangeText={changeValueHandler} value={internalValue} autoCorrect={false} spellCheck={false} smartInsertDelete={false}/>);
});
MoneyTextInput.displayName = 'MoneyTextInput';
